import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltPageHeader } from '@cae/cobalt-react/components/page-header';
import { CobaltIconQrCodeSolid } from '@cae/cobalt-react/icons/solid/CobaltIconQrCodeSolid';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { PrecheckinWidget } from '@features/access-passes/trainee-access-pass/precheckin-widget/PrecheckinWidget';
import { MaintenanceBanner } from '@features/home/components/banners/maintenance-banner/MaintenanceBanner';
import { UpcomingReservationWidget } from '@features/learning-content/upcoming-reservation-widget/UpcomingReservationWidget';
import { UpcomingVisitWidget } from '@features/learning-content/upcoming-visit-widget/UpcomingVisitWidget';
import { LibraryPackageWidget } from '@features/training-content/library-package-widget/LibraryPackageWidget';
import { APP_ROUTES } from '@routes/routes.constants';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useOverviewWidgets } from './hooks/useOverviewWidgets';
import styles from './OverviewPage.module.css';

const OverviewPage = React.memo((): React.ReactElement => {
  const { firstName } = useAuthenticatedUser();
  const navigate = useNavigate();
  const { widgets, isPageLoading, handleWidgetLoad } = useOverviewWidgets();

  const handleAccessPassClick = React.useCallback((): void => {
    navigate(APP_ROUTES.ACCESS_PASS);
  }, [navigate]);

  return (
    <div className={styles.overviewPage} data-testid="overview-page">
      <div className={styles.overviewPageHeaderSection}>
        <div className={styles.overviewMaintenanceBanner}>
          <MaintenanceBanner />
        </div>
        <CobaltPageHeader
          title={
            <span className={styles.overviewPageTitle}>
              {`Hi, ${firstName}`}
              {widgets.precheckin && (
                <CobaltButton
                  variant="ghost"
                  size="x-small"
                  aria-label="Access pass"
                  data-testid="access-pass-icon-button"
                  className={styles.accessPassIconButton}
                  onClick={handleAccessPassClick}
                >
                  <CobaltIconQrCodeSolid size="medium" aria-hidden="true" />
                </CobaltButton>
              )}
            </span>
          }
          className={styles.overviewPageHeader}
        />
      </div>
      {isPageLoading && <ContentLoader />}
      <div
        className={
          isPageLoading
            ? `${styles.overviewPageBody} ${styles.overviewPageBodyHidden}`
            : styles.overviewPageBody
        }
      >
        {widgets.precheckin && (
          <section className={styles.overviewPageBodySection}>
            <PrecheckinWidget
              onLoad={handleWidgetLoad}
              className={styles.overviewPageBodySectionContent}
            />
          </section>
        )}
        {widgets.upcomingReservation && (
          <section className={styles.overviewPageBodySection}>
            <UpcomingReservationWidget onLoad={handleWidgetLoad} />
          </section>
        )}
        {widgets.libraryPackage && (
          <section className={styles.overviewPageBodySection}>
            <LibraryPackageWidget onLoad={handleWidgetLoad} />
          </section>
        )}
        {widgets.upcomingVisit && (
          <section className={styles.overviewPageBodySection}>
            <UpcomingVisitWidget onLoad={handleWidgetLoad} />
          </section>
        )}
      </div>
    </div>
  );
});

OverviewPage.displayName = 'OverviewPage';

export { OverviewPage };
